import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { UnifiedCommunication } from '@/types/customerCommunications';
import { getCtmTrackingLabel } from '@/utils/ctmMetadata';

/**
 * Unified hook to fetch all communications for a customer from 4 sources:
 * - customer_communications (manual logs)
 * - sms_messages
 * - email_messages
 * - call_logs
 *
 * Live updates arrive through the tenant channel in RealtimeProvider, which
 * invalidates ['customer-unified-communications', customerId].
 */
export function useCustomerUnifiedCommunications(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-unified-communications', customerId],
    enabled: !!customerId,
    queryFn: async (): Promise<UnifiedCommunication[]> => {
      if (!customerId) return [];

      // Fetch from all 4 sources in parallel using Promise.allSettled for resilience
      const [manualResult, smsResult, emailResult, callsResult] = await Promise.allSettled([
        supabase
          .from('customer_communications')
          .select('*, creator:profiles!customer_communications_created_by_fkey(full_name)')
          .eq('customer_id', customerId),
        supabase
          .from('sms_messages')
          .select('*, sender:profiles!sms_messages_sender_id_fkey(full_name)')
          .eq('customer_id', customerId),
        supabase
          .from('email_messages')
          .select('*, sender:profiles!email_messages_sender_id_fkey(full_name)')
          .eq('customer_id', customerId),
        supabase
          .from('call_logs')
          .select('*, agent:profiles!call_logs_agent_id_fkey(full_name)')
          .eq('customer_id', customerId),
      ]);

      const unified: UnifiedCommunication[] = [];

      // Map manual communications
      if (manualResult.status === 'fulfilled' && manualResult.value.data) {
        for (const c of manualResult.value.data) {
          unified.push({
            id: c.id,
            type: c.communication_type as UnifiedCommunication['type'],
            source: 'customer_communications',
            direction: c.direction as 'inbound' | 'outbound' | null,
            timestamp: c.created_at || '',
            subject: c.subject || undefined,
            content: c.content || undefined,
            outcome: c.outcome || undefined,
            agent_name: (c.creator as any)?.full_name || undefined,
          });
        }
      }

      // Map SMS
      if (smsResult.status === 'fulfilled' && smsResult.value.data) {
        for (const s of smsResult.value.data) {
          unified.push({
            id: s.id,
            type: 'sms',
            source: 'sms_messages',
            direction: s.direction as 'inbound' | 'outbound',
            timestamp: s.sent_at || s.created_at || '',
            content: s.content || undefined,
            status: s.status || undefined,
            from_number: s.from_number || undefined,
            to_number: s.to_number || undefined,
            agent_name: (s.sender as any)?.full_name || undefined,
            media_urls: (s.media_urls as string[]) || undefined,
          });
        }
      }

      // Map Email
      if (emailResult.status === 'fulfilled' && emailResult.value.data) {
        for (const e of emailResult.value.data) {
          unified.push({
            id: e.id,
            type: 'email',
            source: 'email_messages',
            direction: e.direction as 'inbound' | 'outbound',
            timestamp: e.sent_at || e.created_at || '',
            subject: e.subject || undefined,
            content: e.content || undefined,
            status: e.status || undefined,
            opened_count: e.opened_count || undefined,
            clicked_count: e.clicked_count || undefined,
            bounce_reason: e.bounce_reason || undefined,
            from_email: e.from_email || undefined,
            to_email: e.to_email || undefined,
            agent_name: (e.sender as any)?.full_name || undefined,
            media_urls: (e.attachment_urls as string[]) || undefined,
          });
        }
      }

      // Map Calls
      if (callsResult.status === 'fulfilled' && callsResult.value.data) {
        for (const call of callsResult.value.data) {
          unified.push({
            id: call.id,
            type: 'call',
            source: 'call_logs',
            direction: (call.direction || '').toLowerCase() as 'inbound' | 'outbound',
            timestamp: call.started_at || call.created_at || '',
            duration_seconds: call.duration_seconds || undefined,
            talk_time_seconds: call.talk_time_seconds || undefined,
            recording_available: call.recording_available || undefined,
            recording_activity_id: call.recording_activity_id || undefined,
            disposition: call.disposition || undefined,
            status: call.status || undefined,
            from_number: call.caller_number || undefined,
            to_number: call.receiver_number || undefined,
            tracking_label: getCtmTrackingLabel(call.metadata) || undefined,
            agent_name: (call.agent as any)?.full_name || undefined,
          });
        }
      }

      // Sort by timestamp (newest first)
      return unified.sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    },
  });
}
